/**
 * Booking Storage Utility
 * Handles localStorage for booking data with timestamp validation
 */

const BOOKING_DATA_KEY = 'bookingData';
const PENDING_BOOKING_KEY = 'pendingBooking';
const BOOKING_TIMESTAMP_KEY = 'bookingTimestamp';
const MAX_BOOKING_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Save booking data with timestamp
 */
export const saveBookingData = (bookingData) => {
  const dataWithTimestamp = {
    ...bookingData,
    timestamp: Date.now(),
  };
  localStorage.setItem(BOOKING_DATA_KEY, JSON.stringify(dataWithTimestamp));
  localStorage.setItem(BOOKING_TIMESTAMP_KEY, Date.now().toString());
};

/**
 * Get booking data if it's still valid (not older than 24 hours)
 */
export const getBookingData = () => {
  try {
    const stored = localStorage.getItem(BOOKING_DATA_KEY);
    if (!stored) return null;
    
    const bookingData = JSON.parse(stored);
    const timestamp = bookingData.timestamp || localStorage.getItem(BOOKING_TIMESTAMP_KEY);
    
    // Check if data is stale (older than 24 hours)
    if (timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);
      if (age > MAX_BOOKING_AGE_MS) {
        // Data is too old, clear it
        clearBookingData();
        return null;
      }
    }
    
    // Remove timestamp before returning
    const { timestamp: _, ...data } = bookingData;
    return data;
  } catch (error) {
    console.error('Error reading booking data:', error);
    clearBookingData();
    return null;
  }
};

/**
 * Save pending booking (for unauthenticated users)
 */
export const savePendingBooking = (pendingBooking) => {
  const dataWithTimestamp = {
    ...pendingBooking,
    timestamp: Date.now(),
    doctor_id: pendingBooking.doctor_id || pendingBooking.doctorId,
  };
  localStorage.setItem(PENDING_BOOKING_KEY, JSON.stringify(dataWithTimestamp));
};

/**
 * Get pending booking if it's still valid
 */
export const getPendingBooking = () => {
  try {
    const stored = localStorage.getItem(PENDING_BOOKING_KEY);
    if (!stored) return null;
    
    const pendingBooking = JSON.parse(stored);
    const timestamp = pendingBooking.timestamp;
    
    // Check if data is stale (older than 24 hours)
    if (timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);
      if (age > MAX_BOOKING_AGE_MS) {
        clearPendingBooking();
        return null;
      }
    }
    
    return pendingBooking;
  } catch (error) {
    console.error('Error reading pending booking:', error);
    clearPendingBooking();
    return null;
  }
};

/**
 * Clear booking data
 */
export const clearBookingData = () => {
  localStorage.removeItem(BOOKING_DATA_KEY);
  localStorage.removeItem(BOOKING_TIMESTAMP_KEY);
};

/**
 * Clear pending booking
 */
export const clearPendingBooking = () => {
  localStorage.removeItem(PENDING_BOOKING_KEY);
};

/**
 * Check if there's a valid booking for a specific doctor
 */
export const hasValidBookingForDoctor = (doctorId) => {
  const bookingData = getBookingData();
  const pendingBooking = getPendingBooking();
  
  if (bookingData?.selectedDoctor === doctorId || bookingData?.doctor_id === doctorId) {
    return true;
  }
  
  if (pendingBooking?.doctor_id === doctorId || pendingBooking?.doctorId === doctorId) {
    return true;
  }
  
  return false;
};

/**
 * Clear all booking-related data
 */
export const clearAllBookingData = () => {
  clearBookingData();
  clearPendingBooking();
};

/**
 * Check if booking data should be restored or cleared
 * Returns true if data should be restored, false if it should be cleared
 */
export const shouldRestoreBookingData = (currentDoctorId = null) => {
  const bookingData = getBookingData();
  const pendingBooking = getPendingBooking();
  
  // If no booking data exists, nothing to restore
  if (!bookingData && !pendingBooking) {
    return false;
  }
  
  // If currentDoctorId is provided, only restore if it matches
  if (currentDoctorId) {
    const bookingDoctorId = bookingData?.selectedDoctor || bookingData?.doctor_id || pendingBooking?.doctor_id || pendingBooking?.doctorId;
    if (bookingDoctorId && bookingDoctorId !== currentDoctorId) {
      // Different doctor - clear old data
      clearAllBookingData();
      return false;
    }
  }
  
  // Data exists and is valid
  return true;
};

export default {
  saveBookingData,
  getBookingData,
  savePendingBooking,
  getPendingBooking,
  clearBookingData,
  clearPendingBooking,
  clearAllBookingData,
  hasValidBookingForDoctor,
  shouldRestoreBookingData,
};

